// ----------------------------------------------------------------------------
//   Imports
// ----------------------------------------------------------------------------


// React
import React, { useContext, useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";

// Material UI
import { Button, Box, Stack, IconButton, Menu, MenuItem } from "@mui/material";

// Icons
import MenuIcon from '@mui/icons-material/Menu';

// API
import { fetchUser, userRoles } from "../api/user-service";
import { UserContext } from "../Contexts";



// ----------------------------------------------------------------------------
//   Setup
// ----------------------------------------------------------------------------


const SERVER_URL = process.env.REACT_APP_SERVER_URL;

const btnStyle = {
    mr: 2, 
    mb: 1, 
    fontSize: {
        xs: 7, 
        sm: 10, 
        md: 15
    }
}



// ----------------------------------------------------------------------------
//   Helper Component(s)
// ----------------------------------------------------------------------------


function NavButton( props ) {

    // Context
    const location = useLocation();

    // Component definition
    return (
        <Button component={NavLink}
            variant = { location.pathname === props.to ? 'contained' : 'outlined' }
            to = { props.to }
            sx = { btnStyle }
        >
            { props.children }
        </Button>
    )
}


// ----------------------------------------------------------------------------
//   Main Component
// ----------------------------------------------------------------------------


const Navigation = () => {

    // Context
    const { userData } = useContext( UserContext );
    const location = useLocation();

    const [ menuAnchor, setMenuAnchor ] = useState(null);
    const closeMenu = () => setMenuAnchor(null);

    // Pages shown in both the desktop buttons and the mobile menu
    const links = [
        ...(userData.value?.role === userRoles.ADMIN ? [{ to: "/admin", label: "Admin Dashboard" }] : []),
        { to: "/student",     label: "Practice Videos" },
        { to: "/sing-alongs", label: "Sing-Alongs" },
    ];

    const handleLogout = async () => {
        try {
            const res = await fetch(`${SERVER_URL}/auth/logout`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include'
            });
            if (res.status === 200) {
                window.location.href = '/login';
            }
        } catch(err) {
            console.error('Error logging out:', err);
        }
    }
    
    return (
        <>
            {/* Desktop */}
            <Box sx={{ display: { xs: 'none', sm: 'flex' } }}>
                <Stack direction="row" columnGap="16px" marginX="16px">
                    { links.map(link => <NavButton key={ link.to } to={ link.to }>{ link.label }</NavButton>) }
                </Stack>
                <Button variant="outlined" onClick={handleLogout} sx={btnStyle}>Sign Out</Button>
            </Box>

            {/* Mobile */}
            <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
                <IconButton
                    color="primary"
                    aria-label="Open navigation menu"
                    aria-controls={ menuAnchor ? 'navigation-menu' : undefined }
                    aria-haspopup="true"
                    aria-expanded={ menuAnchor ? 'true' : undefined }
                    onClick={ e => setMenuAnchor(e.currentTarget) }
                >
                    <MenuIcon />
                </IconButton>
                <Menu
                    id="navigation-menu"
                    anchorEl={ menuAnchor }
                    open={ Boolean(menuAnchor) }
                    onClose={ closeMenu }
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                    { links.map(link => (
                        <MenuItem
                            key={ link.to }
                            component={ NavLink }
                            to={ link.to }
                            selected={ location.pathname === link.to }
                            onClick={ closeMenu }
                        >
                            { link.label }
                        </MenuItem>
                    )) }
                    <MenuItem onClick={ () => { closeMenu(); handleLogout(); } }>Sign Out</MenuItem>
                </Menu>
            </Box>
        </>

    )
}



// ----------------------------------------------------------------------------
//   Export(s)
// ----------------------------------------------------------------------------

export default Navigation;
